import { $api } from "@/utils/fetch";
import { privateModelsQueryOptions } from "@/services/private-models-service.ts";
import { useQueryClient } from "@tanstack/react-query";

export const getOrgModelsQueryOptions = () =>
	$api.queryOptions("get", "/api/orgs/current/models");

export const modelAccessQueryOptions = () =>
	$api.queryOptions("get", "/api/orgs/current/models/access");

/**
 * Sets who may use one model - the whole org or specific teams. Writing the
 * "org" level adds an `org_base_models` row, so the base configuration has to
 * be re-read along with the access map.
 */
export const useUpdateModelAccessMutation = (
	callbacks: OrgModelsSaveCallbacks = {},
) => {
	const queryClient = useQueryClient();
	return $api.useMutation("put", "/api/orgs/current/models/access", {
		// Same one-save-at-a-time scope as the base-model replace: both rewrite
		// the model selection, so they must not interleave.
		scope: { id: "org-models-config" },
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: modelAccessQueryOptions().queryKey,
			});
			queryClient.invalidateQueries({
				queryKey: getOrgModelsQueryOptions().queryKey,
			});
			callbacks.onSuccess?.();
		},
		onError: (error) => {
			queryClient.invalidateQueries({
				queryKey: modelAccessQueryOptions().queryKey,
			});
			callbacks.onError?.(error);
		},
	});
};

/**
 * UI-level callbacks for a save. Mirrors `ChatMutationCallbacks` in
 * chats-service.ts: the service owns the cache side effects, the component owns
 * what the admin sees.
 */
export interface OrgModelsSaveCallbacks {
	onSuccess?: () => void;
	/** Receives the failed request's error, for `apiErrorText`. */
	onError?: (error: unknown) => void;
}

export const useUpdateOrgModelsMutation = (
	callbacks: OrgModelsSaveCallbacks = {},
) => {
	const queryClient = useQueryClient();
	return $api.useMutation("put", "/api/orgs/current/models", {
		// One save at a time. TanStack runs same-key mutations in PARALLEL unless
		// they share a scope id — and for a whole-set replace that means the stored
		// selection is decided by commit order rather than by click order.
		scope: { id: "org-models-config" },
		// These live on the mutation, not on the mutate() call, and that is the
		// point: calling mutate() again detaches the observer from the previous
		// mutation, so per-call callbacks of a still-in-flight save never fire —
		// which is how failed saves used to disappear without a toast.
		onSuccess: (data) => {
			// The PUT answers with the configuration it persisted, in the GET's own
			// shape, so seeding the cache from it is exactly what a refetch would
			// have produced — without the refetch, and so without the window in
			// which an in-flight GET lands stale state on top of a newer save.
			queryClient.setQueryData(getOrgModelsQueryOptions().queryKey, data);
			// Private models carry an enabled flag derived from this selection, so
			// that list genuinely has to be re-read.
			queryClient.invalidateQueries({
				queryKey: privateModelsQueryOptions().queryKey,
			});
			callbacks.onSuccess?.();
		},
		onError: (error) => {
			// Re-read rather than leave the UI claiming a save that did not land.
			queryClient.invalidateQueries({
				queryKey: getOrgModelsQueryOptions().queryKey,
			});
			callbacks.onError?.(error);
		},
	});
};
